import * as React from 'react';
import PortalIdParser from 'PortalIdParser';
import UISection from 'UIComponents/section/UISection';
import UIFloatingForm from 'UIComponents/form/UIFloatingForm';
import UIFormControl from 'UIComponents/form/UIFormControl';
import UITextInput from 'UIComponents/input/UITextInput';
import styled, { useTheme } from 'styled-components';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UserNameLabel from 'flow-doctor-lib/components/common/UserNameLabel';
import usePropertiesWithOptions from 'flow-doctor-lib/hooks/usePropertiesWithOptions';
import SkeletonText from 'flow-doctor-lib/components/common/SkeletonText';
import { formatTimestampToPortalTz } from 'flow-doctor-lib/utils/dateTimeUtils';
import { getIsInternal } from 'flow-doctor-lib/constants/Env';
import { formatPropertyValue, isEmptyPropertyValue } from 'flow-doctor-lib/utils/propertyFormatters';
import PropertySourceLabel from 'flow-doctor-lib/components/timeline/labels/PropertySourceLabel';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const getRawOptions = propertyValue => {
  if (propertyValue) {
    return [propertyValue && propertyValue.value];
  }
  return [];
};
const StyledSourceLabel = styled.div.withConfig({
  displayName: "WalmartPropertyFilterEvaluationData__StyledSourceLabel",
  componentId: "b5kgj3-0"
})(({
  theme
}) => `
  margin-left: -${theme.space['300']}
  margin-bottom: ${theme.space['200']}
`);
const WalmartFilterPropertyEvaluationData = ({
  evalData,
  objectTypeId
}) => {
  const rawOptions = React.useMemo(() => getRawOptions(evalData.propertyValue), [evalData.propertyValue]);
  const {
    propertyWrapper,
    labelsForOptions,
    loading: propertyMapLoading,
    error: propertyMapError
  } = usePropertiesWithOptions({
    portalId: PortalIdParser.get(),
    propertyName: evalData.propertyValue && evalData.propertyValue.name,
    objectTypeId,
    rawOptions
  }, {
    skipOptions: !evalData.propertyValue
  });
  const theme = useTheme();
  if (isEmptyPropertyValue(evalData, propertyWrapper)) {
    return /*#__PURE__*/_jsx(UISection, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "flow-doctor-lib.flows.diagnosticWizard.enrollmentAnalysis.filterEvals.popover.missingPropertyValue"
      })
    });
  }
  const isLoadingPropertyReferences = !propertyMapError && (propertyMapLoading || !propertyWrapper);
  return /*#__PURE__*/_jsxs(UIFloatingForm, {
    children: [getIsInternal() && /*#__PURE__*/_jsx(UIFormControl, {
      label: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "flow-doctor-lib.flows.diagnosticWizard.enrollmentAnalysis.filterEvals.popover.fields.internalName"
      }),
      children: /*#__PURE__*/_jsx(UITextInput, {
        readOnly: true,
        value: evalData.propertyValue.name
      })
    }), /*#__PURE__*/_jsx(UIFormControl, {
      label: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "flow-doctor-lib.flows.diagnosticWizard.enrollmentAnalysis.filterEvals.popover.fields.propertyName"
      }),
      children: isLoadingPropertyReferences ? /*#__PURE__*/_jsx(SkeletonText, {
        width: 200
      }) : /*#__PURE__*/_jsx(UITextInput, {
        readOnly: true,
        value: propertyWrapper ? propertyWrapper.property.label : evalData.propertyValue.name
      })
    }), getIsInternal() && /*#__PURE__*/_jsx(UIFormControl, {
      label: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "flow-doctor-lib.flows.diagnosticWizard.enrollmentAnalysis.filterEvals.popover.fields.internalValue"
      }),
      children: /*#__PURE__*/_jsx(UITextInput, {
        readOnly: true,
        value: evalData.propertyValue.value
      })
    }), /*#__PURE__*/_jsx(UIFormControl, {
      label: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "flow-doctor-lib.flows.diagnosticWizard.enrollmentAnalysis.filterEvals.popover.fields.propertyValue"
      }),
      children: isLoadingPropertyReferences ? /*#__PURE__*/_jsx(SkeletonText, {
        width: 200
      }) : /*#__PURE__*/_jsx(UITextInput, {
        readOnly: true,
        value: formatPropertyValue(labelsForOptions[evalData.propertyValue.value] || evalData.propertyValue.value, propertyWrapper)
      })
    }), evalData.matchingFilterValues.length > 0 && /*#__PURE__*/_jsx(UIFormControl, {
      label: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "flow-doctor-lib.flows.diagnosticWizard.enrollmentAnalysis.filterEvals.popover.fields.matchingValues"
      }),
      children: isLoadingPropertyReferences ? /*#__PURE__*/_jsx(SkeletonText, {
        width: 200
      }) : /*#__PURE__*/_jsx(UITextInput, {
        readOnly: true,
        value: evalData.matchingFilterValues.map(value => labelsForOptions[value] || value).join(',')
      })
    }), evalData.propertyValue.updatedByUserId && /*#__PURE__*/_jsx(UIFormControl, {
      label: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "flow-doctor-lib.flows.diagnosticWizard.enrollmentAnalysis.filterEvals.popover.fields.updatedBy"
      }),
      children: /*#__PURE__*/_jsx(UserNameLabel, {
        userId: evalData.propertyValue.updatedByUserId
        // @ts-expect-error hack to be able to use a non-form component in the floating form control without the label appearing empty
        ,
        value: "HACK"
      })
    }), evalData.propertyValue.timestamp && /*#__PURE__*/_jsx(UIFormControl, {
      label: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "flow-doctor-lib.flows.diagnosticWizard.enrollmentAnalysis.filterEvals.popover.fields.timeUpdated"
      }),
      children: /*#__PURE__*/_jsx(UITextInput, {
        readOnly: true,
        value: formatTimestampToPortalTz(evalData.propertyValue.timestamp)
      })
    }), evalData.propertyValue.source && /*#__PURE__*/_jsx(StyledSourceLabel, {
      theme: theme,
      children: /*#__PURE__*/_jsx(PropertySourceLabel, {
        defaultOpen: true,
        source: evalData.propertyValue.source,
        sourceId: evalData.propertyValue.sourceId
      })
    }), getIsInternal() && evalData.propertyValue.sourceId && /*#__PURE__*/_jsx(UIFormControl, {
      label: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "flow-doctor-lib.flows.diagnosticWizard.enrollmentAnalysis.filterEvals.popover.fields.updateSourceId"
      }),
      children: /*#__PURE__*/_jsx(UITextInput, {
        readOnly: true,
        value: evalData.propertyValue.sourceId
      })
    })]
  });
};
export default WalmartFilterPropertyEvaluationData;